import axios from 'axios';
import axiosInstance from './axiosInstance';
import { saveAs } from 'file-saver';
import { format } from 'date-fns';

// const API_BASE_URL = `${process.env.REACT_APP_API_URL}/api/`;
export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
// Pour pre-prod (sous win + docker ubuntu) utiliser localhost

//Fonction de login
export const loginUser = async (username, password) => {
  try {
    const response = await axios.post(`${API_BASE_URL}token/`, {
      username,
      password,
    });
    localStorage.setItem('accessToken', response.data.access);
    localStorage.setItem('refreshToken', response.data.refresh);
  } catch (error) {
    console.error('Error retrieving and saving tokens:', error);
    throw error;
  }
};

// Fonctions pour les utilisateurs
export const fetchCurrentUser = async () => {
  try {
    const response = await axiosInstance.get(`${API_BASE_URL}user`);
    return response.data;
  } catch (error) {
    console.error('Error fetching current user:', error);
    throw error;
  }
};

export const fetchStaffStatus = async () => {
  try {
    const response = await axiosInstance.get(`${API_BASE_URL}user`);
    return response.data.user.is_staff;
  } catch (error) {
    console.error(`Error fetching user staff status:`, error);
    throw error;
  }
};

export const markConfettiSeen = async () => {
  try {
    const response = await axiosInstance.post(`${API_BASE_URL}user/confetti-seen/`);
    return response.data;
  } catch (error) {
    console.error('Error marking confetti as seen:', error);
    throw error;
  }
};

export const createUser = async userData => {
  try {
    const response = await axiosInstance.post(`${API_BASE_URL}user`, userData);
    return response.data;
  } catch (error) {
    console.error('Error creating user:', error);
    throw error;
  }
};

// Fonctions pour les dossiers
export const fetchShipments = async () => {
  try {
    const response = await axiosInstance.get(`${API_BASE_URL}shipments`);
    return response.data;
  } catch (error) {
    console.error('Error fetching shipments:', error);
    throw error;
  }
};

export const fetchShipmentsByPage = async (page, filters = {}, sorting = '', page_size = 200) => {
  const params = new URLSearchParams({
    page,
    ...filters,
    ordering: sorting,
    page_size: page_size,
  });

  Object.keys(filters).forEach(key => {
    if (filters[key]) {
      params.set(key, filters[key]);
    }
  });

  try {
    const response = await axiosInstance.get(`${API_BASE_URL}shipments?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching shipments by page and filters:', error);
    throw error;
  }
};

export const fetchShipmentsByFilters = async (filters = {}, sorting = '') => {
  const params = new URLSearchParams({
    ...filters,
    ordering: sorting,
  });

  Object.keys(filters).forEach(key => {
    if (filters[key]) {
      params.set(key, filters[key]);
    }
  });

  try {
    const response = await axiosInstance.get(`${API_BASE_URL}shipments?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching shipments by filters:', error);
    throw error;
  }
};

export const fetchShipmentByNumber = async shipment_number => {
  try {
    const response = await axiosInstance.get(`${API_BASE_URL}shipments/${shipment_number}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching shipment ${shipment_number}:`, error);
    throw error;
  }
};

export const createShipment = async shipmentData => {
  try {
    const response = await axiosInstance.post(`${API_BASE_URL}shipments/`, shipmentData);
    return response;
  } catch (error) {
    console.error('Error creating shipment:', error);
    throw error;
  }
};

export const updateShipment = async shipmentData => {
  try {
    const response = await axiosInstance.put(
      `${API_BASE_URL}shipments/${shipmentData.shipment_number}/`,
      shipmentData
    );
    return response;
  } catch (error) {
    console.error('Error updating shipment:', error);
    throw error;
  }
};

export const deleteShipment = async shipmentNumber => {
  try {
    const response = await axiosInstance.post(
      `${API_BASE_URL}shipments/${shipmentNumber}/soft-delete/`
    );
    return response;
  } catch (error) {
    console.error('Error deleting shipment:', error);
    throw error;
  }
};

export const restoreShipment = async shipmentNumber => {
  try {
    const response = await axiosInstance.post(
      `${API_BASE_URL}shipments/${shipmentNumber}/restore/`
    );
    return response;
  } catch (error) {
    console.error('Error restoring shipment:', error);
    throw error;
  }
};

export const generateShipmentOrder = async (formData, doc_type = 'carrier_order') => {
  try {
    const endpoint = formData.shipment_number
      ? `${API_BASE_URL}shipments/${formData.shipment_number}/generate-shipment-order/`
      : `${API_BASE_URL}shipments/generate-preview/`;

    const response = await axiosInstance.post(
      endpoint,
      {
        form_data: formData,
        doc_type: doc_type,
      },
      { responseType: 'blob' }
    );

    const blob = new Blob([response.data], { type: 'application/pdf' });

    let filename = 'document.pdf';
    const contentDisposition = response.headers['content-disposition'];
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
      if (match) {
        filename = match[1];
      }
    } else if (formData.shipment_number) {
      filename = `Dossier-numéro-${formData.shipment_number}.pdf`;
    }

    return { blob, filename, response };
  } catch (error) {
    console.error('Erreur lors de la génération du PDF', error);
    if (error.response?.data instanceof Blob) {
      const text = await error.response.data.text();
      try {
        error.response.data = JSON.parse(text);
      } catch {
        error.response.data = { error: text };
      }
    }
    throw error;
  }
};

export const charterShipment = async formData => {
  try {
    const response = await axiosInstance.post(
      `${API_BASE_URL}shipments/${formData.shipment_number}/charter/`,
      formData
    );
    return response;
  } catch (error) {
    console.error("Erreur lors de l'affrètement:", error);
    throw error;
  }
};

export const modifyShipmentWithCarrierNotice = async formData => {
  try {
    const response = await axiosInstance.post(
      `${API_BASE_URL}shipments/${formData.shipment_number}/modify-with-carrier-notice/`,
      formData
    );
    return response;
  } catch (error) {
    console.error('Erreur lors de la modification tps du dossier:', error);
    throw error;
  }
};

export const cancelClient = async formData => {
  try {
    const response = await axiosInstance.post(
      `${API_BASE_URL}shipments/${formData.shipment_number}/cancel-client/`,
      formData
    );
    return response;
  } catch (error) {
    console.error("Erreur lors de l'annulation client du dossier:", error);
    throw error;
  }
};

export const cancelCarrier = async formData => {
  try {
    const response = await axiosInstance.post(
      `${API_BASE_URL}shipments/${formData.shipment_number}/cancel-carrier/`,
      formData
    );
    return response;
  } catch (error) {
    console.error("Erreur lors de l'annulation transporteur du dossier:", error);
    throw error;
  }
};

export const deleteClientCarrier = async formData => {
  try {
    const response = await axiosInstance.post(
      `${API_BASE_URL}shipments/${formData.shipment_number}/delete-client-carrier/`,
      formData
    );
    return response;
  } catch (error) {
    console.error("Erreur lors de l'annulation du dossier:", error);
    throw error;
  }
};

export const payCarrier = async (shipments, invoice_number, payment_date, payment_number) => {
  try {
    const response = await axiosInstance.post(`${API_BASE_URL}pay-carrier/`, {
      shipments: shipments,
      invoice_number: invoice_number,
      payment_date: payment_date,
      payment_number: payment_number,
    });
    return response;
  } catch (error) {
    console.error('Erreur lors du règlement tps des dossiers:', error);
    throw error;
  }
};

export const sendProforma = async (formData, client_emails) => {
  try {
    const response = await axiosInstance.post(
      `${API_BASE_URL}shipments/${formData.shipment_number}/send-proforma/`,
      {
        form_data: formData,
        client_emails: client_emails,
      }
    );
    return response;
  } catch (error) {
    console.error("Erreur lors de l'envoi de la proforma:", error);
    throw error;
  }
};

export const sendClientOrder = async (formData, client_emails) => {
  try {
    const response = await axiosInstance.post(
      `${API_BASE_URL}shipments/${formData.shipment_number}/send-client-order/`,
      {
        form_data: formData,
        client_emails: client_emails,
      }
    );
    return response;
  } catch (error) {
    console.error("Erreur lors de l'envoi du pdf client:", error);
    throw error;
  }
};

export const requestCMR = async formData => {
  try {
    const response = await axiosInstance.post(
      `${API_BASE_URL}shipments/${formData.shipment_number}/request-cmr/`,
      {
        form_data: formData,
      }
    );
    return response;
  } catch (error) {
    console.error("Erreur lors de l'envoi de la demande de CMR:", error);
    throw error;
  }
};

export const duplicateShipment = async (formData, duplication_parameters) => {
  try {
    const response = await axiosInstance.post(
      `${API_BASE_URL}shipments/${formData.shipment_number}/duplicate/`,
      {
        form_data: formData,
        duplication_parameters: duplication_parameters,
      }
    );
    return response;
  } catch (error) {
    console.error('Erreur lors de la duplication:', error);
    throw error;
  }
};

export const fetchChartData = async (startDate, endDate, timeFrame) => {
  try {
    const response = await axiosInstance.get(`${API_BASE_URL}shipments/charts/`, {
      params: {
        start_date: startDate,
        end_date: endDate,
        time_frame: timeFrame,
      },
    });
    return response.data;
  } catch (error) {
    console.error(`Error fetching chart data:`, error);
    throw error;
  }
};

export const fetchPeriodSummary = async (startDate, endDate) => {
  try {
    const response = await axiosInstance.get(`${API_BASE_URL}shipments/period-summary/`, {
      params: {
        start_date: startDate,
        end_date: endDate,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching period summary:', error);
    throw error;
  }
};

export const fetchClientsData = async (startDate, endDate, grouped = false) => {
  try {
    const response = await axiosInstance.get(`${API_BASE_URL}shipments/clients-data/`, {
      params: {
        start_date: startDate,
        end_date: endDate,
        grouped: grouped,
      },
    });
    return response.data;
  } catch (error) {
    console.error(`Error fetching clients data:`, error);
    throw error;
  }
};

export const fetchShipmentsForAutoInvoicing = async () => {
  try {
    const response = await axiosInstance.get(
      `${API_BASE_URL}shipments/shipments-for-auto-invoicing/`
    );
    return response.data;
  } catch (error) {
    console.error(`Error fetching shipments for auto invoicing:`, error);
    throw error;
  }
};

export const fetchShipmentDisputes = async shipmentNumber => {
  try {
    const response = await axiosInstance.get(
      `${API_BASE_URL}shipments/${shipmentNumber}/disputes/`
    );
    return response.data;
  } catch (error) {
    console.error(`Error fetching shipment disputes:`, error);
    throw error;
  }
};

export const updateShipmentDisputes = async (shipmentNumber, disputes) => {
  try {
    const response = await axiosInstance.post(
      `${API_BASE_URL}shipments/${shipmentNumber}/disputes/`,
      disputes
    );
    return response.data;
  } catch (error) {
    console.error(`Error updating shipment disputes:`, error);
    throw error;
  }
};

export const deleteShipmentDispute = async (shipmentNumber, disputeId) => {
  try {
    const response = await axiosInstance.delete(
      `${API_BASE_URL}shipments/${shipmentNumber}/disputes/${disputeId}/`
    );
    return response;
  } catch (error) {
    console.error(`Error deleting shipment dispute:`, error);
    throw error;
  }
};

// Fonctions pour les clients

export const fetchClientsByPage = async (page, filters = {}, sorting = '', page_size = 200) => {
  const params = new URLSearchParams({
    page,
    ...filters,
    ordering: sorting,
    page_size: page_size,
  });

  Object.keys(filters).forEach(key => {
    if (filters[key]) {
      params.set(key, filters[key]);
    }
  });

  try {
    const response = await axiosInstance.get(`${API_BASE_URL}clients?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching clients:', error);
    throw error;
  }
};

export const fetchClientByCode = async client_code => {
  try {
    const response = await axiosInstance.get(
      `${API_BASE_URL}clients/${encodeURIComponent(client_code)}/`
    );
    return response.data;
  } catch (error) {
    console.error(`Error fetching client ${client_code}:`, error);
    throw error;
  }
};

export const fetchClientCodes = async () => {
  try {
    const response = await axiosInstance.get(`${API_BASE_URL}client-codes/`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching client codes:`, error);
    throw error;
  }
};

export const createClient = async clientData => {
  try {
    const response = await axiosInstance.post(`${API_BASE_URL}clients/`, clientData);
    return response;
  } catch (error) {
    console.error('Error creating client:', error);
    throw error;
  }
};

export const updateClient = async clientData => {
  try {
    const response = await axiosInstance.put(
      `${API_BASE_URL}clients/${encodeURIComponent(clientData.client_code)}/`,
      clientData
    );
    return response;
  } catch (error) {
    console.error('Error updating client:', error);
    throw error;
  }
};

export const deleteClient = async clientData => {
  try {
    const response = await axiosInstance.delete(
      `${API_BASE_URL}clients/${encodeURIComponent(clientData.client_code)}/`,
      clientData
    );
    return response;
  } catch (error) {
    console.error('Error deleting client:', error);
    throw error;
  }
};

export const duplicateClient = async (clientCode, newClientCode, newName) => {
  try {
    const response = await axiosInstance.post(
      `${API_BASE_URL}clients/${encodeURIComponent(clientCode)}/duplicate/`,
      {
        new_client_code: newClientCode,
        new_name: newName,
      }
    );
    return response;
  } catch (error) {
    console.error('Error duplicating client:', error);
    throw error;
  }
};

export const fetchClientsOverdueInvoices = async () => {
  try {
    const response = await axiosInstance.get(`${API_BASE_URL}clients-overdue-invoices-count/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching clients overdue invoices:', error);
    throw error;
  }
};

export const remindClients = async (client_list, email_list) => {
  try {
    const response = await axiosInstance.post(`${API_BASE_URL}remind-clients/`, {
      client_list: client_list,
      email_list: email_list,
    });
    return response;
  } catch (error) {
    console.error(`Error reminding clients:`, error);
    throw error;
  }
};

export const fetchClientDisputesData = async (startDate, endDate) => {
  try {
    const response = await axiosInstance.get(`${API_BASE_URL}clients/client-disputes/`, {
      params: {
        start_date: startDate,
        end_date: endDate,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching client disputes:', error);
    throw error;
  }
};

// Fonctions pour les transporteurs

export const fetchCarrierByPage = async (page, filters = {}, sorting = '', page_size = 200) => {
  const params = new URLSearchParams({
    page,
    ...filters,
    ordering: sorting,
    page_size: page_size,
  });

  Object.keys(filters).forEach(key => {
    if (filters[key]) {
      params.set(key, filters[key]);
    }
  });

  try {
    const response = await axiosInstance.get(`${API_BASE_URL}carriers?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching carriers:', error);
    throw error;
  }
};

export const fetchCarrierByCode = async carrier_code => {
  try {
    const response = await axiosInstance.get(
      `${API_BASE_URL}carriers/${encodeURIComponent(carrier_code)}/`
    );
    return response.data;
  } catch (error) {
    console.error(`Error fetching carriers ${carrier_code}:`, error);
    throw error;
  }
};

export const fetchCarrierCodes = async () => {
  try {
    const response = await axiosInstance.get(`${API_BASE_URL}carrier-codes/`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching carrier codes:`, error);
    throw error;
  }
};

export const createCarrier = async carrierData => {
  try {
    const response = await axiosInstance.post(`${API_BASE_URL}carriers/`, carrierData);
    return response;
  } catch (error) {
    console.error('Error creating carrier:', error);
    throw error;
  }
};

export const updateCarrier = async carrierData => {
  try {
    const response = await axiosInstance.put(
      `${API_BASE_URL}carriers/${encodeURIComponent(carrierData.carrier_code)}/`,
      carrierData
    );
    return response;
  } catch (error) {
    console.error('Error updating carrier:', error);
    throw error;
  }
};

export const deleteCarrier = async carrierData => {
  try {
    const response = await axiosInstance.delete(
      `${API_BASE_URL}carriers/${encodeURIComponent(carrierData.carrier_code)}/`,
      carrierData
    );
    return response;
  } catch (error) {
    console.error('Error deleting carrier:', error);
    throw error;
  }
};

export const duplicateCarrier = async (carrierCode, newCarrierCode, newName) => {
  try {
    const response = await axiosInstance.post(
      `${API_BASE_URL}carriers/${encodeURIComponent(carrierCode)}/duplicate/`,
      {
        new_carrier_code: newCarrierCode,
        new_name: newName,
      }
    );
    return response;
  } catch (error) {
    console.error('Error duplicating carrier:', error);
    throw error;
  }
};

// Invoice/credit/proforma

export const generateInvoicePDF = async (
  client_code,
  shipments,
  document_type,
  added_credits = [],
  showPreview = true,
  billing_date = null
) => {
  try {
    const payload = {
      client_code: client_code,
      shipments: shipments,
      document_type: document_type,
      added_credits: added_credits,
    };
    if (billing_date) {
      payload.billing_date = billing_date;
    }
    const response = await axiosInstance.post(`${API_BASE_URL}generate-invoice-pdf/`, payload, {
      responseType: 'blob', // Important to handle PDF response
    });

    if (showPreview) {
      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: 'application/pdf' })
      );
      window.open(url, '_blank');
    }
    return response.status;
  } catch (error) {
    console.error('Erreur lors de la génération du PDF', error);
    if (error.response?.data instanceof Blob) {
      const text = await error.response.data.text();
      try {
        error.response.data = JSON.parse(text);
      } catch {
        error.response.data = { error: text };
      }
    }
    throw error;
  }
};

export const createInvoice = async (
  client_code,
  shipments,
  document_type,
  email_list,
  billing_month,
  billing_period,
  related_invoice_number = '',
  added_credits = [],
  billing_date = null
) => {
  try {
    const payload = {
      client_code: client_code,
      shipments: shipments,
      document_type: document_type,
      related_invoice_number: related_invoice_number,
      email_list: email_list,
      billing_month: billing_month,
      billing_period: billing_period,
      added_credits: added_credits,
    };
    if (billing_date) {
      payload.billing_date = billing_date;
    }
    const response = await axiosInstance.post(`${API_BASE_URL}invoices/`, payload);
    const { invoice_number, email_sent } = response.data;
    return { status: response.status, invoice_number, email_sent };
  } catch (error) {
    console.error('Erreur lors de la création de la facture', error);
    throw error;
  }
};

export const fetchInvoiceByPage = async (page, filters = {}, sorting = '', page_size = 200) => {
  const params = new URLSearchParams({
    page,
    ...filters,
    ordering: sorting,
    page_size: page_size,
  });

  Object.keys(filters).forEach(key => {
    if (filters[key]) {
      params.set(key, filters[key]);
    }
  });

  try {
    const response = await axiosInstance.get(`${API_BASE_URL}invoices?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching invoices:', error);
    throw error;
  }
};

export const fetchInvoiceByNumber = async invoice_number => {
  try {
    const response = await axiosInstance.get(`${API_BASE_URL}invoices/${invoice_number}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching invoice ${invoice_number}:`, error);
    throw error;
  }
};

export const fetchAvailableCredits = async client_code => {
  try {
    const response = await axiosInstance.get(
      `${API_BASE_URL}invoices?client=${client_code}&status=NON+UTILISÉ`
    );
    return response.data;
  } catch (error) {
    console.error(`Error fetching credits for client ${client_code}:`, error);
    throw error;
  }
};

export const fetchInvoiceShipments = async invoice_number => {
  try {
    const response = await axiosInstance.get(
      `${API_BASE_URL}invoices/${invoice_number}/get-shipments/`
    );
    return response.data;
  } catch (error) {
    console.error(`Error fetching invoice shipments for invoice ${invoice_number}:`, error);
    throw error;
  }
};

export const payInvoice = async (invoice_number, payment_number, payment_date, payment_method) => {
  try {
    const response = await axiosInstance.post(`${API_BASE_URL}invoices/${invoice_number}/pay/`, {
      invoice_number: invoice_number,
      payment_number: payment_number,
      payment_date: payment_date,
      payment_method: payment_method,
    });
    return response.data;
  } catch (error) {
    console.error(`Error paying invoice ${invoice_number}:`, error);
    throw error;
  }
};

export const reissueInvoice = async (invoice_number, added_credits) => {
  try {
    const params = {};
    if (added_credits !== undefined) {
      params.added_credits = added_credits;
    }
    const response = await axiosInstance.post(
      `${API_BASE_URL}invoices/${invoice_number}/reissue/`,
      params
    );
    return response.data;
  } catch (error) {
    console.error(`Error reissuing invoice ${invoice_number}:`, error);
    throw error;
  }
};

export const setCreditStatus = async (
  invoice_number,
  status,
  payment_number = '',
  payment_date = null,
  payment_method = ''
) => {
  try {
    const payload = { status };
    if (status === 'UTILISÉ') {
      payload.payment_number = payment_number;
      payload.payment_date = payment_date;
      payload.payment_method = payment_method;
    }
    const response = await axiosInstance.patch(
      `${API_BASE_URL}invoices/${invoice_number}/set-status/`,
      payload
    );
    return response.data;
  } catch (error) {
    console.error(`Error changing invoice status ${invoice_number}:`, error);
    throw error;
  }
};

//  PDF FILES

export const fetchFileByObjectID = async object_id => {
  try {
    const response = await axiosInstance.get(`${API_BASE_URL}pdffiles?object_id=${object_id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching pdf files for object id ${object_id}:`, error);
    throw error;
  }
};

export const fetchFileUrlByID = async file_id => {
  try {
    const isProduction = process.env.NODE_ENV === 'production';

    if (isProduction) {
      // En production, récupère l'URL signée
      const response = await axiosInstance.get(
        `${API_BASE_URL}pdffiles/${file_id}/generate_presigned_url/`
      );
      return response.data.url;
    } else {
      // En développement, récupère l'URL du fichier directement
      const response = await axiosInstance.get(`${API_BASE_URL}pdffiles/${file_id}`);
      return response.data.file; // Assure-toi que cette propriété contient bien l'URL directe du fichier
    }
  } catch (error) {
    console.error(`Error fetching pdf file url with id ${file_id}:`, error);
    throw error;
  }
};

// CONTROLE

export const fetchTodaySummary = async () => {
  try {
    const response = await axiosInstance.get(`${API_BASE_URL}today-summary/`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching today summary:`, error);
    throw error;
  }
};

// EXPORT EXCEL

export const exportDataToExcel = async (data_type, filters = {}, sorting = '') => {
  const params = new URLSearchParams({
    ...filters,
    ordering: sorting,
  });

  Object.keys(filters).forEach(key => {
    if (filters[key]) {
      params.set(key, filters[key]);
    }
  });

  try {
    const response = await axiosInstance.get(
      `${API_BASE_URL}${data_type}s/export?${params.toString()}`,
      {
        responseType: 'blob',
      }
    );

    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const formattedDate = format(new Date(), 'yyyy-MM-dd_HH:mm:ss');
    saveAs(blob, `${data_type}s_${formattedDate}.xlsx`);
    return response;
  } catch (error) {
    console.error('Error exporting data to excel file:', error);
    throw error;
  }
};

// Fetch ADDRESS SUGGESTIONS

export const fetchAddressSuggestions = async value => {
  try {
    const response = await axiosInstance.get(
      `${API_BASE_URL}address-suggestions/?q=${encodeURIComponent(value)}`
    );
    return response.data;
  } catch (error) {
    console.error(`Error fetching address suggestions:`, error);
    throw error;
  }
};

export const deleteAddress = async id => {
  try {
    await axiosInstance.delete(`${API_BASE_URL}addresses/${id}/`);
  } catch (error) {
    console.error(`Error deleting address:`, error);
    throw error;
  }
};

// Fetch column unqiue values

export const fetchColumnUniqueValues = async (model, filters = {}, sorting = '', columnName) => {
  const params = new URLSearchParams({
    model,
    ...filters,
    ordering: sorting,
    column_name: columnName,
  });

  Object.keys(filters).forEach(key => {
    if (filters[key]) {
      params.set(key, filters[key]);
    }
  });

  try {
    const response = await axiosInstance.get(
      `${API_BASE_URL}shipments/column-unique-values?${params.toString()}`
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching column unique values:', error);
    throw error;
  }
};

// Fonctions pour la définition de mot de passe

export const verifyPasswordToken = async token => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}auth/verify-token/?token=${encodeURIComponent(token)}`
    );
    return response.data;
  } catch (error) {
    console.error('Error verifying password token:', error);
    throw error;
  }
};

export const setPassword = async (token, password) => {
  try {
    const response = await axios.post(`${API_BASE_URL}auth/set-password/`, {
      token,
      password,
    });
    return response.data;
  } catch (error) {
    console.error('Error setting password:', error);
    throw error;
  }
};
